import { useState, useEffect, useRef } from 'react';

export function useTableState({ targetTable, token }) {
    const [sortConfig, setSortConfig] = useState([]); // [{ key: 'col', direction: 'asc'|'desc' }]
    const [groupByConfig, setGroupByConfig] = useState([]); // ['col1', 'col2']
    const [searchQuery, setSearchQuery] = useState('');
    const [preferencesLoaded, setPreferencesLoaded] = useState(false);
    
    const debounceTimeout = useRef(null);

    useEffect(() => {
        if (!targetTable || !token) return;
        
        // Load preferences from database
        const loadPreferences = async () => {
            try {
                const key = `TableState_${targetTable}`;
                const res = await fetch(`/api/preferences/${key}`, {
                    headers: { 'Authorization': `Bearer ${token}` }
                });
                if (res.ok) {
                    const data = await res.json();
                    if (data.value) {
                        const parsed = JSON.parse(data.value);
                        if (parsed.sortConfig) setSortConfig(parsed.sortConfig);
                        if (parsed.groupByConfig) setGroupByConfig(parsed.groupByConfig);
                    }
                }
            } catch (err) {
                console.error('Failed to load preferences:', err);
            } finally {
                setPreferencesLoaded(true);
            }
        };

        setPreferencesLoaded(false);
        setSortConfig([]);
        setGroupByConfig([]);
        setSearchQuery('');
        
        loadPreferences();
    }, [targetTable, token]);

    // Save preferences when they change
    useEffect(() => {
        if (!targetTable || !token || !preferencesLoaded) return;

        if (debounceTimeout.current) clearTimeout(debounceTimeout.current);

        debounceTimeout.current = setTimeout(async () => {
            try {
                const key = `TableState_${targetTable}`;
                const payload = JSON.stringify({ sortConfig, groupByConfig });
                await fetch(`/api/preferences/${key}`, {
                    method: 'PUT',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${token}`
                    },
                    body: JSON.stringify({ value: payload })
                });
            } catch (err) {
                console.error('Failed to save preferences:', err);
            }
        }, 1000); // 1s debounce

        return () => clearTimeout(debounceTimeout.current);
    }, [sortConfig, groupByConfig, targetTable, token, preferencesLoaded]);

    const handleSort = (columnName) => {
        setSortConfig(prev => {
            const existing = prev.find(s => s.key === columnName);
            if (!existing) return [...prev, { key: columnName, direction: 'asc' }];
            if (existing.direction === 'asc') return prev.map(s => s.key === columnName ? { ...s, direction: 'desc' } : s);
            return prev.filter(s => s.key !== columnName);
        });
    };

    const addGroup = (columnName) => {
        if (!groupByConfig.includes(columnName)) {
            setGroupByConfig(prev => [...prev, columnName]);
        }
    };

    const removeGroup = (columnName) => {
        setGroupByConfig(prev => prev.filter(g => g !== columnName));
    };

    const moveGroup = (startIndex, endIndex) => {
        setGroupByConfig(prev => {
            const result = Array.from(prev);
            const [removed] = result.splice(startIndex, 1);
            result.splice(endIndex, 0, removed);
            return result;
        });
    };

    return {
        sortConfig,
        groupByConfig,
        searchQuery,
        setSearchQuery,
        handleSort,
        addGroup,
        removeGroup,
        moveGroup,
        preferencesLoaded
    };
}
